import React, { useEffect, useState } from 'react';
import Button, { HEIGHT } from 'design-system/Button';
import { selectDefaultCurrency } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import Animated, { cancelAnimation, Easing, useAnimatedStyle, useSharedValue, withDelay, withRepeat, withSequence, withTiming, } from 'react-native-reanimated';
import useColors from 'hooks/useColors';
import { formatTryFor0 } from 'utils';
import LinearGradient from 'utils/packages/linearGradient';
import { StyleSheet } from 'react-native';
// ==================================================
// CTA Loop effect animation params
// ==================================================
const CTA_LOOP_DURATION = 3000;
const CTA_GLOW_DELAY = CTA_LOOP_DURATION * 1.5;
// ==================================================
// CTA Slam effect animation params
// ==================================================
const START_CTA_SLAM_SCALE = 1;
const CTA_SLAM_SCALE = 1.05;
const CTA_SLAM_DURATION = 300;
// ==================================================
// CTA Glow effect animation params
// ==================================================
const CTA_TARGET_SCALE_X = 1.05;
const CTA_TARGET_SCALE_Y = 1.3;
const CTA_GLOW_DURATION = 1600;
const AnimatedPaywallButton = ({ withCtaSlam, withCtaGlow, isFetching, onPressContinue, enableFreeTrial, pauseUserInteraction, handleUserInteraction, }) => {
    const colors = useColors();
    const userCurrency = useAppSelector(selectDefaultCurrency);
    const [isPressing, setIsPressing] = useState(false);
    // ==================================================
    // Crossfade animation between trial / continue states
    // ==================================================
    const crossfadeProgress = useSharedValue(enableFreeTrial ? 1 : 0);
    useEffect(() => {
        crossfadeProgress.value = withTiming(enableFreeTrial ? 1 : 0, {
            duration: 300,
            easing: Easing.inOut(Easing.ease),
        });
    }, [crossfadeProgress, enableFreeTrial]);
    const trialButtonStyle = useAnimatedStyle(() => ({
        opacity: crossfadeProgress.value,
    }));
    const continueButtonStyle = useAnimatedStyle(() => ({
        opacity: 1 - crossfadeProgress.value,
    }));
    // ==================================================
    // CTA Slam effect animation
    // ==================================================
    const ctaSlamScale = useSharedValue(START_CTA_SLAM_SCALE);
    const ctaSlamAnimatedStyle = useAnimatedStyle(() => ({
        transform: [{ scale: ctaSlamScale.value }],
    }));
    useEffect(() => {
        if (withCtaSlam) {
            if (enableFreeTrial && !isPressing && !isFetching) {
                ctaSlamScale.value = withDelay(CTA_LOOP_DURATION, withRepeat(withSequence(withTiming(CTA_SLAM_SCALE, {
                    duration: CTA_SLAM_DURATION,
                    easing: Easing.inOut(Easing.ease),
                }), withTiming(START_CTA_SLAM_SCALE, {
                    duration: CTA_SLAM_DURATION * 2,
                    easing: Easing.elastic(3),
                }), withTiming(START_CTA_SLAM_SCALE, {
                    duration: CTA_LOOP_DURATION - CTA_SLAM_DURATION * 3,
                })), -1, false));
            }
            else {
                ctaSlamScale.value = withTiming(START_CTA_SLAM_SCALE, {
                    duration: 500,
                    easing: Easing.inOut(Easing.ease),
                });
            }
        }
        return () => {
            cancelAnimation(ctaSlamScale);
            ctaSlamScale.value = START_CTA_SLAM_SCALE;
        };
    }, [ctaSlamScale, enableFreeTrial, isFetching, isPressing, withCtaSlam]);
    // ==================================================
    // CTA Glow effect animation
    // ==================================================
    const ctaGlowScaleX = useSharedValue(1);
    const ctaGlowScaleY = useSharedValue(1);
    const ctaGlowOpacity = useSharedValue(0);
    const ctaGlowAnimatedStyle = useAnimatedStyle(() => ({
        transform: [
            { scaleX: ctaGlowScaleX.value },
            { scaleY: ctaGlowScaleY.value },
        ],
        opacity: ctaGlowOpacity.value,
    }));
    useEffect(() => {
        if (withCtaGlow) {
            if (enableFreeTrial && !isPressing && !isFetching) {
                ctaGlowScaleX.value = withDelay(CTA_GLOW_DELAY, withRepeat(withSequence(withTiming(CTA_TARGET_SCALE_X, {
                    duration: CTA_GLOW_DURATION,
                }), withTiming(1, {
                    duration: 0,
                }), withTiming(1, {
                    duration: CTA_LOOP_DURATION - CTA_GLOW_DURATION,
                })), -1, false));
                ctaGlowScaleY.value = withDelay(CTA_GLOW_DELAY, withRepeat(withSequence(withTiming(CTA_TARGET_SCALE_Y, {
                    duration: CTA_GLOW_DURATION,
                }), withTiming(1, {
                    duration: 0,
                }), withTiming(1, {
                    duration: CTA_LOOP_DURATION - CTA_GLOW_DURATION,
                })), -1, false));
                ctaGlowOpacity.value = withDelay(CTA_GLOW_DELAY, withRepeat(withSequence(withTiming(1, {
                    duration: 0,
                }), withTiming(0, {
                    duration: CTA_GLOW_DURATION,
                }), withTiming(0, {
                    duration: CTA_LOOP_DURATION - CTA_GLOW_DURATION,
                })), -1, false));
            }
            else {
                ctaGlowScaleX.value = withTiming(1, {
                    duration: 500,
                    easing: Easing.inOut(Easing.ease),
                });
                ctaGlowScaleY.value = withTiming(1, {
                    duration: 500,
                    easing: Easing.inOut(Easing.ease),
                });
                ctaGlowOpacity.value = withTiming(0, {
                    duration: 500,
                    easing: Easing.inOut(Easing.ease),
                });
            }
        }
        return () => {
            cancelAnimation(ctaGlowScaleX);
            cancelAnimation(ctaGlowScaleY);
            cancelAnimation(ctaGlowOpacity);
            ctaGlowScaleX.value = 1;
            ctaGlowScaleY.value = 1;
            ctaGlowOpacity.value = 0;
        };
    }, [
        ctaGlowOpacity,
        ctaGlowScaleX,
        ctaGlowScaleY,
        enableFreeTrial,
        isFetching,
        isPressing,
        withCtaGlow,
    ]);
    return (<Animated.View style={!!withCtaSlam && ctaSlamAnimatedStyle}>
      <Animated.View style={trialButtonStyle} pointerEvents={enableFreeTrial ? 'auto' : 'none'}>
        <Button custom brand isFetching={isFetching} onPress={onPressContinue} title={formatTryFor0(userCurrency)} gradientColors={[
            '#15C9D2',
            String(colors.gradients.ultimate.colors[0]),
        ]} onPressIn={() => {
            setIsPressing(true);
            pauseUserInteraction?.();
        }} onPressOut={() => {
            setIsPressing(false);
            handleUserInteraction?.();
        }}/>
      </Animated.View>
      <Animated.View style={[styles.continueButtonOverlay, continueButtonStyle]} pointerEvents={enableFreeTrial ? 'none' : 'auto'}>
        <Button custom brand isFetching={isFetching} onPress={onPressContinue} title="Continue" onPressIn={() => {
            setIsPressing(true);
            pauseUserInteraction?.();
        }} onPressOut={() => {
            setIsPressing(false);
            handleUserInteraction?.();
        }}/>
      </Animated.View>
      <Animated.View style={[styles.glowContainer, ctaGlowAnimatedStyle]}>
        <LinearGradient colors={[
            String(colors.gradients.ultimate.colors[0]),
            String(colors.gradients.pro.colors[0]),
        ]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={[styles.linearGradient]}/>
      </Animated.View>
    </Animated.View>);
};
const styles = StyleSheet.create({
    continueButtonOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    },
    glowContainer: {
        height: HEIGHT,
        width: '100%',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: -1,
        borderRadius: HEIGHT / 2,
    },
    linearGradient: {
        flex: 1,
        borderRadius: HEIGHT / 2,
        opacity: 0.5,
    },
});
export default AnimatedPaywallButton;
